import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'

import Icon, { IconName } from '@/components/atomic/icon'
import { cn } from '@/utils/shadcn/utils'

import {
  type BannerSize,
  BannerVariant,
  bannerSizes,
  type IconSize,
} from './types'

// Base container component with error banner styling (copied from error-banner molecule)
interface BannerContainerProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  variant: BannerVariant
  size?: BannerSize
}

export const BannerContainer = forwardRef<HTMLDivElement, BannerContainerProps>(
  ({ children, variant, size = 'medium', className, ...props }, ref) => {
    const config = bannerSizes[size]

    // Error styling
    const errorStyles = cn(
      'flex items-center rounded-md w-full',
      'bg-moment-red-200 text-moment-red-800',
      'dark:bg-moment-red-1000 dark:text-moment-red-400',
      config.container,
    )

    // Warning styling
    const warningStyles = cn(
      'flex items-center rounded-md w-full',
      'bg-moment-orange-200 text-moment-orange-700',
      'dark:bg-moment-orange-1000 dark:text-moment-orange-500',
      config.container,
    )

    // Info styling
    const infoStyles = cn(
      'flex items-center rounded-md w-full',
      'bg-moment-blue-200 text-moment-blue-700',
      'dark:bg-moment-blue-1000 dark:text-moment-blue-500',
      config.container,
    )

    const style = {
      [BannerVariant.Error]: errorStyles,
      [BannerVariant.Warning]: warningStyles,
      [BannerVariant.Info]: infoStyles,
    }[variant]

    return (
      <div ref={ref} className={cn('h-auto', style, className)} {...props}>
        {children}
      </div>
    )
  },
)
BannerContainer.displayName = 'BannerContainer'

// Icon wrapper component (copied from error-banner molecule)
interface BannerIconContainerProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

export const BannerIconContainer = forwardRef<
  HTMLDivElement,
  BannerIconContainerProps
>(({ children, className, ...props }, ref) => {
  return (
    <div ref={ref} className={cn('flex-shrink-0', className)} {...props}>
      {children}
    </div>
  )
})
BannerIconContainer.displayName = 'BannerIconContainer'

// Icon component
interface BannerIconProps extends HTMLAttributes<HTMLDivElement> {
  variant: BannerVariant
  size?: IconSize
}

export const BannerIcon = forwardRef<HTMLDivElement, BannerIconProps>(
  ({ variant, size = 'small', className, ...props }, ref) => {
    // Error icon styling
    const errorIconStyles = cn(
      'flex-shrink-0',
      'text-moment-red-700 dark:text-moment-red-400',
      className,
    )

    // Warning icon styling
    const warningIconStyles = cn(
      'flex-shrink-0',
      'text-moment-orange-700 dark:text-moment-orange-500',
      className,
    )

    // Info icon styling
    const infoIconStyles = cn(
      'flex-shrink-0',
      'text-moment-blue-700 dark:text-moment-blue-500',
      className,
    )

    const iconStyles = {
      [BannerVariant.Error]: errorIconStyles,
      [BannerVariant.Warning]: warningIconStyles,
      [BannerVariant.Info]: infoIconStyles,
    }[variant]

    const iconName = {
      [BannerVariant.Error]: IconName.ExclamationCircleIcon,
      [BannerVariant.Warning]: IconName.ExclamationTriangleIcon,
      [BannerVariant.Info]: IconName.InformationCircleIcon,
    }[variant]

    const outline = variant === BannerVariant.Error

    return (
      <div ref={ref} className={iconStyles} {...props}>
        <Icon iconName={iconName} outline={outline} size={size} />
      </div>
    )
  },
)
BannerIcon.displayName = 'BannerIcon'

// Content wrapper
interface BannerContentProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

export const BannerContent = forwardRef<HTMLDivElement, BannerContentProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex-1 max-h-20 overflow-y-auto', className)}
        {...props}
      >
        {children}
      </div>
    )
  },
)
BannerContent.displayName = 'BannerContent'

// Text component
interface BannerTextProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  variant: BannerVariant
  size?: BannerSize
}

export const BannerText = forwardRef<HTMLDivElement, BannerTextProps>(
  ({ children, variant, size = 'medium', className, ...props }, ref) => {
    const config = bannerSizes[size]
    const { TextComponent } = config

    // Error text styling
    const errorTextStyles = cn(
      'text-moment-red-700 dark:text-moment-red-400',
      className,
    )

    // Warning text styling
    const warningTextStyles = cn(
      'text-moment-orange-700 dark:text-moment-orange-500',
      className,
    )

    // Info text styling
    const infoTextStyles = cn(
      'text-moment-blue-700 dark:text-moment-blue-400',
      className,
    )

    const textStyles = {
      [BannerVariant.Error]: errorTextStyles,
      [BannerVariant.Warning]: warningTextStyles,
      [BannerVariant.Info]: infoTextStyles,
    }[variant]

    return (
      <div ref={ref} className={textStyles} {...props}>
        <TextComponent>{children}</TextComponent>
      </div>
    )
  },
)
BannerText.displayName = 'BannerText'

// Wrapper component
interface BannerWrapperProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  variant: BannerVariant
}

export const BannerWrapper = forwardRef<HTMLDivElement, BannerWrapperProps>(
  ({ children, variant, className, ...props }, ref) => {
    if (variant === BannerVariant.Error) {
      return (
        <div ref={ref} className={cn(className)} {...props}>
          {children}
        </div>
      )
    }
    return <>{children}</>
  },
)
BannerWrapper.displayName = 'BannerWrapper'
